import {
  ApplicationConfig,
  computed,
  importProvidersFrom,
  inject,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import {
  tablerBuilding,
  tablerCalendar,
  tablerCalendarStats,
  tablerChartBar,
  tablerChartPie,
  tablerDownload,
  tablerLayoutGridAdd,
  tablerMap2,
  tablerMapPin,
  tablerWorld,
} from '@ng-icons/tabler-icons';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HttpClient, HttpXhrBackend, withXhr } from '@angular/common/http';
import { XhrFactory } from '@angular/common';
import { routes } from './app.routes';
import { LoggerModule } from 'ngx-logger';
import { environment } from '../environments/environment';
import { HttpClientInMemoryWebApiModule } from 'angular-in-memory-web-api';
import { InMemoryDataService } from './core/services/in-memory-data.service';
import { REAL_HTTP_CLIENT } from './core/tokens/real-http-client.token';
import { API_HOST, provideOnrsrUi } from '@onrsr/ui/core';
import {
  MENU_ITEMS,
  LAYOUT_AUTH_SERVICE,
  LAYOUT_PROFILE_SERVICE,
  LAYOUT_BRANDING,
} from '@onrsr/ui/layout';
import type { MenuItem } from '@onrsr/ui/layout';
import { AuthService, provideAuth } from '@onrsr/ui/auth';
import { provideMapboxGL } from 'ngx-mapbox-gl';
import { ProfileService } from './core/services/profile.service';

const menuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'tablerChartBar',
    route: '/dashboard',
  },
  {
    id: 'myorganisation',
    label: 'My Organisation',
    icon: 'tablerBuilding',
    children: [
      {
        id: 'organisation-profile',
        label: 'Organisation Profile',
        route: '/organisation-profile',
      },
      {
        id: 'portal-users',
        label: 'Portal Users',
        route: '/organisation/portal-users',
      },
    ],
  },
  {
    id: 'occurrences',
    label: 'Occurrences',
    icon: 'tablerLayoutGridAdd',
    children: [
      {
        id: 'search-occurrences',
        label: 'Occurrences',
        route: '/occurrences',
      },
      {
        id: 'report-occurrence',
        label: 'Report Occurrence',
        route: '/occurrences/report',
      },
      {
        id: 'category-c-annual-submissions',
        label: 'Category C Annual Submissions',
        route: '/occurrences/categorycannualsubmissions',
      },
      {
        id: 'report-nsw-d&a-test',
        label: 'Report NSW D&A Test',
        route: '/occurrences/reportnswdatest',
      },
      {
        id: 'occurrence-export',
        label: 'Occurrence Export',
        route: '/occurrences/export',
      },
    ],
  },
  {
    id: 'ontology',
    label: 'Ontology',
    icon: 'tablerMapPin',
    children: [
      {
        id: 'search-line-sections',
        label: 'Line Sections',
        route: '/ontology/searchlinesections',
      },
      {
        id: 'search-defined-locations',
        label: 'Defined Locations',
        route: '/ontology/searchdefinedlocations',
      },
      {
        id: 'search-level-crossing-sites',
        label: 'Level Crossing Sites',
        route: '/ontology/searchlevelcrossingsites',
      },
      {
        id: 'network-data',
        label: 'Network Data',
        route: '/ontology/networkdata',
      },
    ],
  },
  {
    id: 'notification-of-change',
    label: 'Notification of Change',
    icon: 'tablerDownload',
    children: [
      {
        id: 'search-notification-of-change',
        label: 'Search Notification of Change',
        route: '/notificationofchange',
      },
    ],
  },
  {
    id: 'monthly-returns',
    label: 'Monthly Returns',
    icon: 'tablerCalendar',
    route: '/monthlyreturns/searchmonthlyreturns',
  },
  {
    id: 'annual-returns',
    label: 'Annual Returns',
    icon: 'tablerCalendarStats',
    route: '/annualreturns/searchannualreturns',
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'tablerChartPie',
    children: [
      {
        id: 'performance-benchmarking-report',
        label: 'Performance Benchmarking',
        route: '/reports/performancebenchmarkingreport',
      },
    ],
  },
  {
    id: 'mapbox-demo',
    label: 'Mapbox Demo',
    icon: 'tablerMap2',
    route: '/mapbox-demo',
  },
  {
    id: 'openstreetmap-demo',
    label: 'OpenStreetMap Demo',
    icon: 'tablerWorld',
    route: '/openstreetmap-demo',
  },
];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideMapboxGL({ accessToken: environment.mapbox.accessToken }),
    provideIcons({
      tablerBuilding,
      tablerCalendar,
      tablerCalendarStats,
      tablerChartBar,
      tablerChartPie,
      tablerDownload,
      tablerLayoutGridAdd,
      tablerMap2,
      tablerMapPin,
      tablerWorld,
    }),
    ...provideOnrsrUi(),
    {
      provide: LAYOUT_BRANDING,
      useValue: { logoUrl: 'images/onrsr_portal_logo_dark.png', title: 'ONRSR PORTAL' },
    },
    {
      provide: REAL_HTTP_CLIENT,
      useFactory: (xhrFactory: XhrFactory) => new HttpClient(new HttpXhrBackend(xhrFactory)),
      deps: [XhrFactory],
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    importProvidersFrom(
      HttpClientInMemoryWebApiModule.forRoot(InMemoryDataService, {
        apiBase: 'api/v1.0/',
        passThruUnknownUrl: true,
      }),
      LoggerModule.forRoot({
        serverLoggingUrl: environment.logging.apiEndpoint,
        level: environment.logging.level,
        serverLogLevel: environment.logging.serverLogLevel,
      }),
    ),
    provideAuth({
      clientId: environment.azure.clientId,
      authority: `https://login.microsoftonline.com/${environment.azure.tenantId}`,
      knownAuthorities: [],
      redirectUri: environment.azure.redirectUri,
      postLogoutRedirectUri: environment.azure.redirectUri,
      scopes: [`api://${environment.azure.clientId}/access_as_user`],
    }),
    {
      provide: MENU_ITEMS,
      useValue: menuItems,
    },
    {
      provide: LAYOUT_AUTH_SERVICE,
      useExisting: AuthService,
    },
    {
      provide: LAYOUT_PROFILE_SERVICE,
      useFactory: () => {
        const authService = inject(AuthService);
        const profileService = inject(ProfileService);
        return {
          fullName: computed(() => {
            const user = authService.currentUser();
            return user?.name ?? user?.username ?? '';
          }),
          avatarImage: computed(() => profileService.avatarUrl() ?? null),
        };
      },
    },
    { provide: API_HOST, useValue: environment.apiHost },
  ],
};
